import React, { useRef, useState, DragEvent, ChangeEvent } from 'react';
import styled, { css } from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { uploadImage } from '@/api/store';

interface Props {
   /** Current Cloudinary URL (empty string when nothing uploaded yet). */
   value: string;
   onChange: (url: string) => void;
   label?: string;
   /** Restrict the file picker — defaults to images. Pass e.g. "image/*,application/pdf" if you need PDFs too. */
   accept?: string;
   /** Max size in MB (default 10 to match the server-side multer limit). */
   maxSizeMb?: number;
   disabled?: boolean;
   /** Smaller drop-zone height for space-constrained modals. */
   compact?: boolean;
   /** Notifies the parent while an upload is in flight (so it can disable submit). */
   onUploadingChange?: (uploading: boolean) => void;
}

const FileUploader: React.FC<Props> = ({
   value,
   onChange,
   label,
   accept = 'image/*',
   maxSizeMb = 10,
   disabled = false,
   compact = false,
   onUploadingChange,
}) => {
   const inputRef = useRef<HTMLInputElement>(null);
   const [isUploading, setIsUploading] = useState(false);
   const [progress, setProgress] = useState(0);
   const [isDragOver, setIsDragOver] = useState(false);

   const handleFile = async (file: File) => {
      if (disabled || isUploading) return;
      if (file.size > maxSizeMb * 1024 * 1024) {
         toast.error(`File too large — max ${maxSizeMb}MB`);
         return;
      }
      setIsUploading(true);
      onUploadingChange?.(true);
      setProgress(0);
      try {
         const { imageUrl } = await uploadImage(file, (p) => setProgress(p));
         onChange(imageUrl);
      } catch (err: any) {
         toast.error(err?.response?.data?.error || 'Upload failed');
      } finally {
         setIsUploading(false);
         onUploadingChange?.(false);
         setProgress(0);
      }
   };

   const onPick = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleFile(file);
      // Reset so picking the same file again still fires onChange
      e.target.value = '';
   };

   const onDrop = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files?.[0];
      if (file) void handleFile(file);
   };

   const onDragOver = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      if (!isDragOver) setIsDragOver(true);
   };

   const onDragLeave = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      setIsDragOver(false);
   };

   const openPicker = () => {
      if (disabled || isUploading) return;
      inputRef.current?.click();
   };

   return (
      <Wrap>
         {label && <Label>{label}</Label>}
         <input
            ref={inputRef}
            type="file"
            accept={accept}
            onChange={onPick}
            style={{ display: 'none' }}
         />
         {value ? (
            <FilledZone>
               <ThumbWrap>
                  <Thumb src={value} alt={label ?? 'uploaded file'} />
               </ThumbWrap>
               <FilledMeta>
                  <FilledTitle>Uploaded</FilledTitle>
                  <FilledLink href={value} target="_blank" rel="noopener noreferrer">
                     View original ↗
                  </FilledLink>
               </FilledMeta>
               <FilledActions>
                  <ReplaceBtn
                     type="button"
                     onClick={openPicker}
                     disabled={disabled || isUploading}
                  >
                     Replace
                  </ReplaceBtn>
                  <RemoveBtn
                     type="button"
                     onClick={() => onChange('')}
                     disabled={disabled || isUploading}
                     aria-label="Remove uploaded file"
                  >
                     Remove
                  </RemoveBtn>
               </FilledActions>
            </FilledZone>
         ) : (
            <DropZone
               $dragOver={isDragOver}
               $disabled={disabled || isUploading}
               $compact={compact}
               onClick={openPicker}
               onDrop={onDrop}
               onDragOver={onDragOver}
               onDragLeave={onDragLeave}
            >
               {isUploading ? (
                  <ProgressWrap>
                     {/* The % tracks bytes sent to our server only; the server
                         then forwards to Cloudinary. So at 100% bytes we show
                         "Processing…" instead of a misleading "done". */}
                     <ProgressLbl>
                        {progress < 100 ? `Uploading… ${progress}%` : 'Processing…'}
                     </ProgressLbl>
                     <ProgressBar>
                        <ProgressFill style={{ width: `${progress}%` }} />
                     </ProgressBar>
                  </ProgressWrap>
               ) : (
                  <DropZoneInner>
                     <UploadIcon>⬆</UploadIcon>
                     <DropZoneText>
                        <strong>Click to upload</strong> or drag and drop
                     </DropZoneText>
                     <DropZoneHint>
                        {accept === 'image/*' ? 'JPG, PNG, GIF' : accept} — max {maxSizeMb}MB
                     </DropZoneHint>
                  </DropZoneInner>
               )}
            </DropZone>
         )}
      </Wrap>
   );
};

const Wrap = styled.div`
   display: flex;
   flex-direction: column;
   gap: 4px;
`;

const Label = styled.label`
   font-size: 12px;
   font-weight: 600;
   color: ${colors.GRAY1};
`;

const DropZone = styled.div<{
   $dragOver: boolean;
   $disabled: boolean;
   $compact?: boolean;
}>`
   display: flex;
   align-items: center;
   justify-content: center;
   min-height: ${({ $compact }) => ($compact ? '56px' : '96px')};
   padding: ${({ $compact }) => ($compact ? '8px' : '12px')};
   border: 2px dashed
      ${({ $dragOver }) => ($dragOver ? colors.PRIMARY : colors.GRAY12)};
   border-radius: 8px;
   background: ${({ $dragOver }) =>
      $dragOver ? `${colors.PRIMARY}08` : 'var(--app-surface)'};
   cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
   opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
   transition: all 0.15s;
   ${({ $disabled }) =>
      !$disabled &&
      css`
         &:hover {
            border-color: ${colors.PRIMARY};
            background: ${colors.PRIMARY}06;
         }
      `}
`;

const DropZoneInner = styled.div`
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 4px;
   text-align: center;
   pointer-events: none;
`;

const UploadIcon = styled.span`
   font-size: 18px;
   color: ${colors.GRAY3};
`;

const DropZoneText = styled.p`
   font-size: 12px;
   color: ${colors.GRAY1};
`;

const DropZoneHint = styled.p`
   font-size: 10px;
   color: ${colors.GRAY5};
`;

const ProgressWrap = styled.div`
   width: 100%;
   display: flex;
   flex-direction: column;
   gap: 6px;
   align-items: center;
   pointer-events: none;
`;

const ProgressLbl = styled.p`
   font-size: 11px;
   color: ${colors.PRIMARY};
   font-weight: 700;
`;

const ProgressBar = styled.div`
   width: 80%;
   height: 4px;
   background: ${colors.GRAY13};
   border-radius: 4px;
   overflow: hidden;
`;

const ProgressFill = styled.div`
   height: 100%;
   background: ${colors.PRIMARY};
   transition: width 0.15s linear;
`;

const FilledZone = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
   padding: 8px 12px;
   border: 1px solid ${colors.SUCCESS}40;
   background: ${colors.SUCCESS}08;
   border-radius: 8px;
`;

const ThumbWrap = styled.div`
   width: 48px;
   height: 48px;
   border-radius: 6px;
   overflow: hidden;
   flex-shrink: 0;
   background: ${colors.GRAY14};
`;

const Thumb = styled.img`
   width: 100%;
   height: 100%;
   object-fit: cover;
`;

const FilledMeta = styled.div`
   flex: 1;
   display: flex;
   flex-direction: column;
   gap: 2px;
   min-width: 0;
`;

const FilledTitle = styled.span`
   font-size: 12px;
   font-weight: 700;
   color: ${colors.SUCCESS};
`;

const FilledLink = styled.a`
   font-size: 11px;
   color: ${colors.PRIMARY};
   text-decoration: none;
   &:hover { text-decoration: underline; }
`;

const FilledActions = styled.div`
   display: flex;
   gap: 6px;
   flex-shrink: 0;
`;

const ReplaceBtn = styled.button`
   background: transparent;
   border: 1px solid ${colors.GRAY12};
   color: ${colors.GRAY3};
   padding: 5px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   white-space: nowrap;
   &:hover:not(:disabled) {
      border-color: ${colors.PRIMARY};
      color: ${colors.PRIMARY};
   }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const RemoveBtn = styled.button`
   background: transparent;
   border: 1px solid ${colors.DANGER}50;
   color: ${colors.DANGER};
   padding: 5px 10px;
   border-radius: 6px;
   font-size: 11px;
   font-weight: 600;
   cursor: pointer;
   white-space: nowrap;
   &:hover:not(:disabled) { background: ${colors.DANGER}10; }
   &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export default FileUploader;
